import React, { useState, useMemo, useEffect } from 'react';
import { formatCurrency } from '../../utils/formatters';
import { supabase } from '../../lib/supabase';

interface SlotBreakdown {
    slots: number;
    grossRevenue: number;
    partnerShare: number; // 30% repasse
    watchProfit: number;  // 70% lucro da Watch
}

interface SlotVenueData {
    id: string;
    valor_ticket_slot: number;
    qt_slot: number;
    created_at: string;
    updated_at: string;
}

const VenuePlansManagementSection: React.FC = () => {
    const [ticketValue, setTicketValue] = useState<string>('');
    const [slotQuantity, setSlotQuantity] = useState<string>('');
    const [savedTicketValue, setSavedTicketValue] = useState<number | null>(null);
    const [savedSlotQuantity, setSavedSlotQuantity] = useState<number | null>(null);
    const [message, setMessage] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [recordId, setRecordId] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<string | null>(null);
    const [clearModalOpen, setClearModalOpen] = useState<boolean>(false);

    // Carregar dados do banco ao iniciar
    useEffect(() => {
        loadSlotVenueData();
    }, []);

    const loadSlotVenueData = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('slot_venue')
                .select('*')
                .limit(1)
                .single();

            if (error) {
                // Se não encontrou registro, não é erro
                if (error.code === 'PGRST116') {
                    console.log('Nenhum registro encontrado em slot_venue');
                    return;
                }
                throw error;
            }

            if (data) {
                const slotData = data as SlotVenueData;
                setRecordId(slotData.id);
                setTicketValue(slotData.valor_ticket_slot.toString());
                setSlotQuantity(slotData.qt_slot.toString());
                setSavedTicketValue(slotData.valor_ticket_slot);
                setSavedSlotQuantity(slotData.qt_slot);
                setLastUpdated(slotData.updated_at);
            }
        } catch (err) {
            console.error('Erro ao carregar dados de slot_venue:', err);
            setMessage('❌ Erro ao carregar dados salvos.');
            setTimeout(() => setMessage(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    // Gerar tabela de breakdown para diferentes quantidades de slots
    const slotBreakdowns = useMemo<SlotBreakdown[]>(() => {
        if (!savedTicketValue || savedTicketValue <= 0 || !savedSlotQuantity || savedSlotQuantity <= 0) return [];

        // Gerar array de 1 até a quantidade definida pelo usuário
        const breakdowns: SlotBreakdown[] = [];
        for (let slots = 1; slots <= savedSlotQuantity; slots++) {
            const grossRevenue = savedTicketValue * slots;
            const partnerShare = grossRevenue * 0.30; // 30% repasse ao parceiro
            const watchProfit = grossRevenue * 0.70;  // 70% lucro da Watch

            breakdowns.push({
                slots,
                grossRevenue,
                partnerShare,
                watchProfit
            });
        }

        return breakdowns;
    }, [savedTicketValue, savedSlotQuantity]);

    const handleSave = async () => {
        const value = parseFloat(ticketValue);
        const quantity = parseInt(slotQuantity);

        if (isNaN(value) || value <= 0) {
            setMessage('❌ Por favor, insira um valor do ticket válido maior que zero.');
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        if (isNaN(quantity) || quantity <= 0) {
            setMessage('❌ Por favor, insira uma quantidade de slots válida maior que zero.');
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        if (quantity > 1000) {
            setMessage('❌ A quantidade máxima de slots é 1000.');
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        try {
            setLoading(true);
            setMessage('🔄 Salvando...');

            let result;

            if (recordId) {
                // Atualizar registro existente
                result = await supabase
                    .from('slot_venue')
                    .update({
                        valor_ticket_slot: value,
                        qt_slot: quantity
                    })
                    .eq('id', recordId)
                    .select()
                    .single();
            } else {
                // Inserir novo registro
                result = await supabase
                    .from('slot_venue')
                    .insert({
                        valor_ticket_slot: value,
                        qt_slot: quantity
                    })
                    .select()
                    .single();
            }

            if (result.error) {
                throw result.error;
            }

            const savedData = result.data as SlotVenueData;
            setRecordId(savedData.id);
            setSavedTicketValue(savedData.valor_ticket_slot);
            setSavedSlotQuantity(savedData.qt_slot);
            setLastUpdated(savedData.updated_at);

            setMessage('✅ Dados salvos com sucesso!');
            setTimeout(() => setMessage(''), 3000);

        } catch (err) {
            console.error('Erro ao salvar dados:', err);
            setMessage('❌ Erro ao salvar dados: ' + (err as Error).message);
            setTimeout(() => setMessage(''), 5000);
        } finally {
            setLoading(false);
        }
    };

    const handleClearClick = () => {
        if (!recordId) {
            setTicketValue('');
            setSlotQuantity('');
            setSavedTicketValue(null);
            setSavedSlotQuantity(null);
            return;
        }
        setClearModalOpen(true);
    };

    const handleConfirmClear = async () => {
        setClearModalOpen(false);

        try {
            setLoading(true);
            setMessage('🔄 Limpando...');

            const { error } = await supabase
                .from('slot_venue')
                .update({
                    valor_ticket_slot: 0,
                    qt_slot: 0
                })
                .eq('id', recordId);

            if (error) throw error;

            setTicketValue('');
            setSlotQuantity('');
            setSavedTicketValue(null);
            setSavedSlotQuantity(null);
            setMessage('✅ Valores zerados com sucesso!');
            setTimeout(() => setMessage(''), 3000);

        } catch (err) {
            console.error('Erro ao limpar dados:', err);
            setMessage('❌ Erro ao limpar dados.');
            setTimeout(() => setMessage(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelClear = () => {
        setClearModalOpen(false);
    };

    return (
        <div className="w-full bg-white rounded-lg shadow p-6 mb-8">
            <div className="mb-6">
                <p className="text-gray-600">
                    Configure o valor do ticket por slot de anúncio Venue e visualize a projeção de arrecadação e divisão de receitas.
                </p>
                {lastUpdated && (
                    <p className="text-xs text-gray-400 mt-1">
                        Última atualização: {new Date(lastUpdated).toLocaleString('pt-BR')}
                    </p>
                )}
            </div>

            {/* Formulário de cadastro do valor do ticket */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                    {/* Campo Valor do Ticket */}
                    <div className="w-full">
                        <label htmlFor="ticketValue" className="block text-sm font-medium text-gray-700 mb-2">
                            Valor do Ticket por Slot (R$)
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                                R$
                            </span>
                            <input
                                type="number"
                                id="ticketValue"
                                step="0.01"
                                min="0"
                                value={ticketValue}
                                onChange={(e) => setTicketValue(e.target.value)}
                                placeholder="0,00"
                                disabled={loading}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-lg disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Valor cobrado por cada slot de anúncio
                        </p>
                    </div>

                    {/* Campo Quantidade de Slots */}
                    <div className="w-full">
                        <label htmlFor="slotQuantity" className="block text-sm font-medium text-gray-700 mb-2">
                            Quantidade de Slots
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                </svg>
                            </span>
                            <input
                                type="number"
                                id="slotQuantity"
                                min="1"
                                max="1000"
                                value={slotQuantity}
                                onChange={(e) => setSlotQuantity(e.target.value)}
                                placeholder="Ex: 10"
                                disabled={loading}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-lg disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Quantidade total de slots (máx: 1000)
                        </p>
                    </div>

                    {/* Botões */}
                    <div className="w-full lg:w-auto flex flex-col">
                        <label className="block text-sm font-medium text-transparent mb-2 select-none">
                            Ações
                        </label>
                        <div className="flex gap-2">
                            <button
                                onClick={handleSave}
                                disabled={loading}
                                className="flex-1 lg:flex-none px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                                <span className="flex items-center justify-center gap-2">
                                    {loading ? (
                                        <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                        </svg>
                                    )}
                                    {loading ? 'Salvando...' : 'Salvar'}
                                </span>
                            </button>

                            {savedTicketValue && savedSlotQuantity && (
                                <button
                                    onClick={handleClearClick}
                                    disabled={loading}
                                    className="flex-1 lg:flex-none px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Limpar
                                </button>
                            )}
                        </div>
                        <p className="text-xs text-transparent mt-1 select-none">placeholder</p>
                    </div>
                </div>

                {/* Exibir valores salvos */}
                {savedTicketValue && savedSlotQuantity && (
                    <div className="mt-4 p-4 bg-white rounded-lg border border-orange-200 shadow-sm">
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm text-gray-600">Valor do ticket:</span>
                                <span className="text-xl font-bold text-orange-600">{formatCurrency(savedTicketValue)}</span>
                            </div>
                            <div className="h-6 w-px bg-gray-300 hidden md:block"></div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Quantidade de slots:</span>
                                <span className="text-xl font-bold text-amber-600">{savedSlotQuantity}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Mensagem de feedback */}
            {message && (
                <div className={`p-4 rounded-lg text-sm mb-6 ${message.includes('✅')
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : message.includes('🔄')
                        ? 'bg-blue-100 text-blue-800 border border-blue-200'
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                    {message}
                </div>
            )}

            {/* Loading inicial */}
            {loading && !savedTicketValue && (
                <div className="text-center py-8">
                    <svg className="w-8 h-8 mx-auto text-orange-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <p className="text-gray-500 mt-2">Carregando dados...</p>
                </div>
            )}

            {/* Tabela de projeção de arrecadação */}
            {savedTicketValue && savedSlotQuantity && slotBreakdowns.length > 0 && (
                <div className="mt-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                            <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Projeção de Arrecadação por Quantidade de Slots
                        </h3>
                        <p className="text-sm text-gray-500">
                            Valores calculados com base no ticket de {formatCurrency(savedTicketValue)} por slot × {savedSlotQuantity} slots
                        </p>
                    </div>

                    {/* Cards de resumo */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-blue-100 text-sm">Arrecadação Bruta</p>
                                    <p className="text-xs text-blue-200 mt-1">100% do valor total por slot</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-5 text-white shadow-lg">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-amber-100 text-sm">Repasse ao Parceiro</p>
                                    <p className="text-xs text-amber-200 mt-1">30% do valor arrecadado</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-5 text-white shadow-lg">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-emerald-100 text-sm">Lucro Watch</p>
                                    <p className="text-xs text-emerald-200 mt-1">70% do valor arrecadado</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabela */}
                    <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gradient-to-r from-gray-800 to-gray-900 text-white">
                                    <th className="px-6 py-4 text-left text-sm font-semibold">
                                        Qtd. Slots
                                    </th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold">
                                        <span className="flex items-center justify-end gap-2">
                                            <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                                            Arrecadação Bruta
                                        </span>
                                    </th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold">
                                        <span className="flex items-center justify-end gap-2">
                                            <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                                            Repasse 30%
                                        </span>
                                    </th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold">
                                        <span className="flex items-center justify-end gap-2">
                                            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                                            Lucro Watch 70%
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {slotBreakdowns.map((item, index) => (
                                    <tr
                                        key={item.slots}
                                        className={`
                                            border-b border-gray-100 transition-colors hover:bg-orange-50
                                            ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                        `}
                                    >
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center justify-center bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm px-3 py-1 rounded-full min-w-[60px]">
                                                {item.slots}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <span className="text-gray-800 font-semibold text-lg">
                                                {formatCurrency(item.grossRevenue)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <span className="text-orange-600 font-semibold text-lg">
                                                {formatCurrency(item.partnerShare)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <span className="text-green-600 font-bold text-lg">
                                                {formatCurrency(item.watchProfit)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Nota de rodapé */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="text-sm text-gray-600">
                                <p className="font-medium text-gray-700 mb-1">Sobre a divisão de receitas:</p>
                                <ul className="list-disc list-inside space-y-1">
                                    <li><span className="font-medium text-orange-600">30% Repasse</span> - Valor destinado ao parceiro Venue que disponibiliza a tela para anúncios</li>
                                    <li><span className="font-medium text-green-600">70% Lucro Watch</span> - Receita líquida da Watch após o repasse ao parceiro</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Estado vazio */}
            {!savedTicketValue && !loading && (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-600 mb-2">Nenhum valor configurado</h3>
                    <p className="text-gray-500 max-w-md mx-auto">
                        Insira o valor do ticket por slot e a quantidade desejada, depois clique em "Salvar" para armazenar os dados.
                    </p>
                </div>
            )}

            {/* Modal de Confirmação de Limpeza */}
            {clearModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-5 text-white">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-full">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-bold">Confirmar Limpeza</h3>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                            <p className="text-gray-700 mb-4">
                                Tem certeza que deseja <strong className="text-red-600">zerar todos os valores</strong> salvos no banco de dados?
                            </p>

                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                                <div className="flex items-start gap-3">
                                    <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div className="text-sm text-amber-800">
                                        <p className="font-medium mb-1">Esta ação irá:</p>
                                        <ul className="list-disc list-inside space-y-1">
                                            <li>Zerar o valor do ticket por slot</li>
                                            <li>Zerar a quantidade de slots</li>
                                            <li>Remover a tabela de projeção</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Valores atuais */}
                            {savedTicketValue && savedSlotQuantity && (
                                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                    <p className="text-xs text-gray-500 uppercase mb-2">Valores atuais que serão zerados:</p>
                                    <div className="flex flex-wrap gap-4">
                                        <div>
                                            <span className="text-sm text-gray-600">Valor do ticket:</span>
                                            <span className="ml-2 font-bold text-orange-600">{formatCurrency(savedTicketValue)}</span>
                                        </div>
                                        <div>
                                            <span className="text-sm text-gray-600">Quantidade:</span>
                                            <span className="ml-2 font-bold text-amber-600">{savedSlotQuantity} slots</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="border-t p-4 bg-gray-50 flex gap-3 justify-end">
                            <button
                                onClick={handleCancelClear}
                                className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirmClear}
                                className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all shadow-md hover:shadow-lg font-medium flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Confirmar Limpeza
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VenuePlansManagementSection;
